/**
 * Setup — connections panel rendered inside Settings → Connections.
 *
 * Currently surfaces Google (Calendar / Gmail / Contacts) OAuth onboarding.
 * Telegram, Email, and Voice connectivity show as status pills only — those
 * surfaces are configured via env or per-channel webhooks today.
 */
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { setup } from "../api/client";
import { Button, Card, Mono, Pill } from "../components/v3";

type StatusTone = "ok" | "warn" | "off";

export function Setup() {
  const queryClient = useQueryClient();
  const { data: setupStatus, isLoading } = useQuery({
    queryKey: ["setup-status"],
    queryFn: () => setup.status(),
    refetchInterval: 10_000,
  });

  const { data: googleDetail } = useQuery({
    queryKey: ["google-status"],
    queryFn: () => setup.googleStatus(),
    refetchInterval: 5_000,
  });

  // Scroll to hash anchor on mount (used by SetupItem deep-links from Chat).
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      el?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-3 max-w-[680px]">
        <div className="skeleton h-12" />
        <div className="skeleton h-12" />
        <div className="skeleton h-48" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-[680px]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatusRow
          label="AI"
          tone={setupStatus?.llm.status === "connected" ? "ok" : "off"}
          detail={setupStatus?.llm.model ?? undefined}
        />
        <StatusRow
          label="Google"
          tone={
            setupStatus?.google.status === "connected"
              ? "ok"
              : setupStatus?.google.status === "needs_auth"
              ? "warn"
              : "off"
          }
          detail={setupStatus?.google.email ?? undefined}
        />
        <StatusRow
          label="Telegram"
          tone={setupStatus?.telegram.status === "connected" ? "ok" : "off"}
        />
        <StatusRow
          label="Voice"
          tone={setupStatus?.voice.status === "configured" ? "ok" : "off"}
        />
        <StatusRow
          label="Email"
          tone={setupStatus?.email.status === "configured" ? "ok" : "off"}
        />
      </div>

      <GoogleSection
        setupStatus={setupStatus?.google}
        googleDetail={googleDetail}
        onRefresh={() => {
          queryClient.invalidateQueries({ queryKey: ["setup-status"] });
          queryClient.invalidateQueries({ queryKey: ["google-status"] });
        }}
      />
    </div>
  );
}

// ── Status pill row (used in the overview grid) ───────────────────────────

function StatusRow({
  label,
  tone,
  detail,
}: {
  label: string;
  tone: StatusTone;
  detail?: string;
}) {
  const dotColor = {
    ok: "bg-amber",
    warn: "bg-amber-deep",
    off: "bg-t4",
  }[tone];
  const labelText = {
    ok: "Connected",
    warn: "Needs auth",
    off: "Off",
  }[tone];
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-md border border-line bg-white/[0.015]">
      <span className={`size-1.5 rounded-full ${dotColor}`} />
      <div className="flex-1 min-w-0">
        <div className="text-t1 text-sm font-medium">{label}</div>
        {detail && (
          <div className="text-t3 text-xs truncate font-mono">{detail}</div>
        )}
      </div>
      <Mono>{labelText}</Mono>
    </div>
  );
}

// ── Google OAuth section ───────────────────────────────────────────────────

function GoogleSection({
  setupStatus,
  googleDetail,
  onRefresh,
}: {
  setupStatus?: { status: string; email: string | null };
  googleDetail?: {
    connected: boolean;
    email: string | null;
    connected_at: string | null;
  } | null;
  onRefresh: () => void;
}) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const isConnected = googleDetail?.connected ?? false;
  const needsAuth = setupStatus?.status === "needs_auth";
  const needsConfig = setupStatus?.status === "not_configured";

  async function handleSaveCredentials() {
    if (!clientId.trim() || !clientSecret.trim() || saving) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await setup.googleSaveConfig(clientId.trim(), clientSecret.trim());
      setSaveSuccess(true);
      setClientId("");
      setClientSecret("");
      onRefresh();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    }
    setSaving(false);
  }

  async function handleDisconnect() {
    if (disconnecting) return;
    setDisconnecting(true);
    try {
      await setup.googleDisconnect();
      onRefresh();
    } catch {
      /* ignore */
    }
    setDisconnecting(false);
  }

  return (
    <Card padding="md" className="scroll-mt-24" {...({ id: "google" } as any)}>
      <div className="flex items-center justify-between mb-1">
        <Mono>Google · Calendar · Gmail · Contacts</Mono>
        <Pill tone={isConnected ? "ok" : needsAuth ? "warn" : "neutral"}>
          {isConnected ? "Connected" : needsAuth ? "Needs auth" : "Not configured"}
        </Pill>
      </div>
      <p className="text-t2 text-sm leading-relaxed mb-4 max-w-[58ch]">
        Vooice reads and writes your calendar, summarizes inbox, and looks up
        contacts. One-time OAuth setup; revocable any time.
      </p>

      {isConnected && (
        <Card padding="md" glow className="border-amber/30">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-t1 font-medium">{googleDetail?.email}</div>
              {googleDetail?.connected_at && (
                <Mono className="block mt-1">
                  Since{" "}
                  {new Date(googleDetail.connected_at).toLocaleDateString()}
                </Mono>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="text-rose hover:text-rose"
            >
              {disconnecting ? "…" : "Disconnect"}
            </Button>
          </div>
        </Card>
      )}

      {!isConnected && needsAuth && (
        <Card padding="md" className="border-amber/40">
          <Mono tone="amber" className="block mb-2">
            Almost there
          </Mono>
          <p className="text-t2 text-sm mb-4">
            Credentials are saved. Connect your account to finish.
          </p>
          <Button as="a" href="/api/oauth/google/start" variant="amber" size="md">
            Connect Google account
          </Button>
        </Card>
      )}

      {!isConnected && needsConfig && !saveSuccess && (
        <div className="space-y-4">
          <GoogleSetupSteps />
          <div className="space-y-3 pt-4 border-t border-line-soft">
            <Mono className="block">Paste credentials</Mono>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Client ID"
              className="w-full px-3 py-2 bg-bg-2 border border-line rounded-md text-sm text-t1 placeholder:text-t4 font-mono focus:outline-none focus:border-amber/40"
              autoComplete="off"
            />
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="Client Secret"
              className="w-full px-3 py-2 bg-bg-2 border border-line rounded-md text-sm text-t1 placeholder:text-t4 font-mono focus:outline-none focus:border-amber/40"
              autoComplete="off"
            />
            <Button
              variant="amber"
              size="md"
              onClick={handleSaveCredentials}
              disabled={!clientId.trim() || !clientSecret.trim() || saving}
            >
              {saving ? "Saving…" : "Save credentials"}
            </Button>
            {saveError && (
              <div className="text-rose text-sm border border-rose/30 bg-rose/[0.06] rounded-md px-3 py-2">
                {saveError}
              </div>
            )}
          </div>
        </div>
      )}

      {!isConnected && saveSuccess && (
        <Card padding="md" className="border-amber/40">
          <Mono tone="amber" className="block mb-2">
            Credentials saved
          </Mono>
          <p className="text-t2 text-sm mb-4">
            Now connect your Google account to finish setup.
          </p>
          <Button as="a" href="/api/oauth/google/start" variant="amber" size="md">
            Connect Google account
          </Button>
        </Card>
      )}
    </Card>
  );
}

function GoogleSetupSteps() {
  const [expanded, setExpanded] = useState(false);
  const callbackUrl = `${window.location.origin}/api/oauth/google/callback`;

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-amber hover:underline text-sm transition-colors"
      >
        {expanded
          ? "Hide instructions"
          : "How to get Google OAuth credentials →"}
      </button>

      {expanded && (
        <ol className="mt-3 space-y-2 text-sm text-t2 list-decimal list-inside leading-relaxed">
          <li>
            Go to{" "}
            <a
              href="https://console.cloud.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber hover:underline"
            >
              Google Cloud Console
            </a>{" "}
            and create a new project (or select one).
          </li>
          <li>
            Enable these APIs:{" "}
            <a
              href="https://console.cloud.google.com/apis/library/calendar-json.googleapis.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber hover:underline"
            >
              Calendar
            </a>
            ,{" "}
            <a
              href="https://console.cloud.google.com/apis/library/gmail.googleapis.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber hover:underline"
            >
              Gmail
            </a>
            ,{" "}
            <a
              href="https://console.cloud.google.com/apis/library/people.googleapis.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber hover:underline"
            >
              Contacts
            </a>
            .
          </li>
          <li>
            Go to{" "}
            <a
              href="https://console.cloud.google.com/apis/credentials"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber hover:underline"
            >
              Credentials
            </a>{" "}
            → Create Credentials → OAuth 2.0 Client ID → Web Application.
          </li>
          <li>
            Set the authorized redirect URI to:
            <code className="block mt-2 px-3 py-2 bg-bg-2 border border-line rounded-md text-amber font-mono text-xs select-all">
              {callbackUrl}
            </code>
          </li>
          <li>
            Copy the Client ID and Client Secret, then paste them below.
          </li>
        </ol>
      )}
    </div>
  );
}
